import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { SidenavService } from '@layout/sidenav/sidenav.service';

export type Theme = 'fury-default' | 'fury-light' | 'fury-dark' | 'fury-flat' | 'fury-earthy';
export type ThemePosition = 'fixed' | 'above-fixed' | 'static';

export interface ThemeConfig {
  navigation: 'side' | 'top';
  sidenavUserVisible: boolean;
  toolbarPosition: ThemePosition;
  footerVisible: boolean;
  footerPosition: ThemePosition;
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  private _themeSubject = new BehaviorSubject<[Theme | null, Theme]>([null, 'fury-flat']);
  theme$ = this._themeSubject.asObservable();
  activeTheme$ = this.theme$.pipe(
    map(theme => theme[1])
  );
  private _configSubject = new BehaviorSubject<ThemeConfig>({
    navigation: 'side',
    sidenavUserVisible: true,
    toolbarPosition: 'fixed',
    footerVisible: false,
    footerPosition: 'fixed'
  });
  config$ = this._configSubject.asObservable();

  /**
   * Estado atual, para quem precisa dele de uma vez — o painel de configuração monta com ele o
   * corpo que grava a cada alteração. Ler o `BehaviorSubject` por uma subscrição só para pegar o
   * valor corrente deixaria o painel guardando uma cópia do estado que já vive aqui.
   */
  get config(): ThemeConfig {
    return this._configSubject.getValue();
  }

  get theme(): Theme {
    return this._themeSubject.getValue()[1];
  }

  private readonly sidenavService = inject(SidenavService);

  /**
   * A posição do menu que o layout deve usar — que nem sempre é a preferida.
   *
   * <p>No celular é sempre lateral, qualquer que seja a preferência salva: o menu superior é uma
   * régua horizontal de itens, que numa tela estreita não cabe. A preferência do usuário fica
   * intacta no banco e volta a valer no desktop; o que muda é só o que se desenha agora.</p>
   */
  readonly navegacaoEfetiva$: Observable<'side' | 'top'> = combineLatest([
    this.config$,
    this.sidenavService.mobile$,
  ]).pipe(
    map(([config, mobile]) => mobile ? 'side' as const : config.navigation),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: false }),
  );

  constructor() {
    this.setTheme('fury-flat');
  }

  setTheme(theme: Theme) {
    this._themeSubject.next([this._themeSubject.getValue()[1], theme]);
  }

  setNavigation(navigation: 'side' | 'top') {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      navigation
    });
  }

  setSidenavUserVisible(sidenavUserVisible: boolean) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      sidenavUserVisible
    });
  }

  setToolbarPosition(toolbarPosition: ThemePosition) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      toolbarPosition
    });
  }

  setFooterVisible(footerVisible: boolean) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerVisible
    });
  }

  setFooterPosition(footerPosition: ThemePosition) {
    this._configSubject.next({
      ...this._configSubject.getValue(),
      footerPosition
    });
  }

  setStyle(style: 'default' | 'flat' | 'dark' | 'light' | 'top' | string) {
    switch (style) {
      case 'flat': {
        this._configSubject.next({
          navigation: 'side',
          sidenavUserVisible: false,
          toolbarPosition: 'static',
          footerVisible: false,
          footerPosition: 'static'
        });

        this.sidenavService.setCollapsed(true);
        this.setTheme('fury-flat');
        break;
      }

      case 'dark': {
        this.setTheme('fury-dark');
        break;
      }

      case 'light': {
        this._configSubject.next({
          navigation: 'side',
          sidenavUserVisible: false,
          toolbarPosition: 'static',
          footerVisible: false,
          footerPosition: 'static'
        });

        this.setTheme('fury-light');
        break;
      }

      case 'earthy': {
        this.setTheme('fury-earthy');
        break;
      }

      case 'top': {
        this._configSubject.next({
          navigation: 'top',
          sidenavUserVisible: false,
          toolbarPosition: 'fixed',
          footerVisible: false,
          footerPosition: 'fixed'
        });
        break;
      }
    }
  }
}
